import React, { FunctionComponent, useEffect, useState } from "react";

import { T } from "services/lang/LanguageService";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DropdownChangeParams } from "primereact/dropdown";
import { DashboardEditorProps } from "../../ComponentType";
import { DatasetLoadingConf, IPIDControllerProps } from "../charts/interfaces";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";

const DefaultDatasetLoadingConf: DatasetLoadingConf = {
  lead: 0,
  lag: 6,
  lagUnit: "HOURS",
  leadUnit: "HOURS",
  alignUnit: null,
};

const PIDControllerEditor: FunctionComponent<DashboardEditorProps<IPIDControllerProps>> = ({
  config,
  onConfigChange,
}) => {
  const conf = config ?? {};
  const [currentConfig, setCurrentConfig] = useState(conf);

  const handleTextChange = (name: keyof IPIDControllerProps, value: string) => {
    (conf[name] as string) = value;
    setCurrentConfig({ ...conf });
  };

  const handleNumberChange = (name: keyof IPIDControllerProps, value: number | null) => {
    (conf[name] as string | number) = value ?? "";
    setCurrentConfig({ ...conf });
  };
  const handleLoadConf = (e: InputSwitchChangeParams): void => {
    const tempConfig = structuredClone(currentConfig);
    if (e.value) {
      tempConfig.loadConf = defaultLoadConf;
    } else {
      delete tempConfig.loadConf;
    }
    setCurrentConfig(tempConfig);
  };

  const handleLoadConfChange = (e: InputNumberValueChangeParams | DropdownChangeParams) => {
    const key = e.target.id as keyof DatasetLoadingConf;
    let { value } = e.target;
    if (Number.isNaN(value)) value = value.toString().toUpperCase();
    setCurrentConfig(prev => ({
      ...prev,
      loadConf: prev.loadConf
        ? { ...prev.loadConf, [key]: value }
        : { ...DefaultDatasetLoadingConf, [key]: value },
    }));
  };

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  return (
    <div className="flex flex-column flex-wrap gap-3">
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="kp">K {T("word.pid-controller.proportional")}</label>
          <InputText
            id="kp"
            placeholder={T("word.dataset-id")}
            value={conf.kp}
            onChange={e => handleTextChange("kp", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="ki">K {T("word.pid-controller.integral")}</label>
          <InputText
            id="ki"
            placeholder={T("word.dataset-id")}
            value={conf.ki}
            onChange={e => handleTextChange("ki", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="kd">K {T("word.pid-controller.derivative")}</label>
          <InputText
            id="kd"
            placeholder={T("word.dataset-id")}
            value={conf.kd}
            onChange={e => handleTextChange("kd", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="kall">K {T("word.all")}</label>
          <InputText
            id="kall"
            placeholder={T("word.dataset-id")}
            value={conf.kall}
            onChange={e => handleTextChange("kall", e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-column w-3">
        <label htmlFor="delta">Δ</label>
        <InputText
          id="delta"
          placeholder={T("word.dataset-id")}
          value={conf.delta}
          onChange={e => handleTextChange("delta", e.target.value)}
        />
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="controlVariable">{T("word.pid-controller.control-variable")}</label>
          <InputText
            id="controlVariable"
            placeholder={T("word.dataset-id")}
            value={conf.controlVariable}
            onChange={e => handleTextChange("controlVariable", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="sp">{T("word.pid-controller.set-point")}</label>
          <InputText
            id="sp"
            placeholder={T("word.dataset-id")}
            value={conf.setPoint}
            onChange={e => handleTextChange("setPoint", e.target.value)}
          />
        </div>

        <div className="flex flex-column">
          <label htmlFor="mp">{T("word.pid-controller.manipulated-variable")}</label>
          <InputText
            id="mp"
            placeholder={T("word.dataset-id")}
            value={conf.manipulatedVariable}
            onChange={e => handleTextChange("manipulatedVariable", e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <div className="flex flex-column">
          <label htmlFor="min">Min</label>
          <InputText
            id="min"
            placeholder={T("word.dataset-id")}
            value={conf.min}
            onChange={e => handleTextChange("min", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="max">Max</label>
          <InputText
            id="max"
            placeholder={T("word.dataset-id")}
            value={conf.max}
            onChange={e => handleTextChange("max", e.target.value)}
          />
        </div>
        <div className="flex flex-column">
          <label htmlFor="updateFrequency">{T("load-conf.update-frequency")} ({T("time.seconds")})</label>
          <InputNumber
            id="updateFrequency"
            placeholder={T("time.seconds")}
            value={conf.updateFrequency}
            onValueChange={e => handleNumberChange("updateFrequency", e.value)}
            mode="decimal"
          />
        </div>
      </div>
      <div className="flex flex-row">
        <div className="gap-2 flex flex-column">
          <label htmlFor="loadConf">{T("load-conf.load-conf")}</label>
          <InputSwitch
            id="loadConf"
            checked={currentConfig.loadConf !== undefined}
            onChange={handleLoadConf}
          />
        </div>
        {currentConfig.loadConf !== undefined && (
          <>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lag">{T("load-conf.lag")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lag"
                  value={currentConfig.loadConf.lag as number}
                  onValueChange={handleLoadConfChange}
                />
                <TimeUnitDropdown
                  id="lagUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.lagUnit}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="lead">{T("load-conf.lead")}</label>
              <div className="p-inputgroup">
                <InputNumber
                  id="lead"
                  value={currentConfig.loadConf.lead as number}
                  onValueChange={handleLoadConfChange}
                />
                <TimeUnitDropdown
                  id="leadUnit"
                  className="p-inputgroup-addon p-0"
                  value={currentConfig.loadConf.leadUnit}
                  onChange={handleLoadConfChange}
                />
              </div>
            </div>
            <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
              <label htmlFor="alignUnit">{T("load-conf.align-unit")}</label>
              <TimeUnitDropdown
                id="alignUnit"
                className="w-full"
                value={currentConfig.loadConf.alignUnit ? currentConfig.loadConf.alignUnit : ""}
                onChange={handleLoadConfChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PIDControllerEditor;
